import { memo, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import _ from 'lodash';
import { useToggle } from '~/hooks';
import { Box, ConfirmPopup, Row, Text } from '~/commonComponents';
import { Colors } from '~/themes/colors';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import ThumbsUpIcon from '~/images/thumb-up-icon.svg';
import ExclamationIcon from '~/images/exclamation-circle-icon.svg';
import { FeedbackItem } from './FeedbackItem';
import { IFeedback } from '../../../Types';

interface ICloseJobFeedbackPopupProps {
  onClose: () => void;
  onSubmit: (feedback: IFeedback) => void;
}

const CloseJobFeedbackPopupComponent = ({ onClose, onSubmit }: ICloseJobFeedbackPopupProps) => {
  const [selectedPositive, setSelectedPositive] = useState<string[]>([]);
  const [selectedNegative, setSelectedNegative] = useState<string[]>([]);
  const [showOtherInput, showOtherInputToggle] = useToggle(false);
  const [otherText, setOtherText] = useState('');

  const feedbackOptionsPositive = RemoteConfigUtils.getCloseJobFeedbackPositive();
  const feedbackOptionsNegative = RemoteConfigUtils.getCloseJobFeedbackNegative();

  const toggleOption = useCallback(
    (option: string, type: 'positive' | 'negative') => {
      const setter = type === 'positive' ? setSelectedPositive : setSelectedNegative;
      const selected = type === 'positive' ? selectedPositive : selectedNegative;

      if (_.toLower(option ?? '') === 'other') {
        const willBeSelected = !selected.includes(option);
        showOtherInputToggle.set(willBeSelected);
        // Clear otherText when Other is deselected
        if (!willBeSelected) setOtherText('');
      }

      setter((prev) =>
        prev.includes(option) ? prev.filter((item) => item !== option) : [...prev, option],
      );
    },
    [
      selectedPositive,
      selectedNegative,
      setSelectedPositive,
      setSelectedNegative,
      showOtherInputToggle,
    ],
  );

  return (
    // @ts-expect-error -- ConfirmPopup still written in JavaScript
    <ConfirmPopup
      title="How Was Your Experience?"
      description="Help us improve GoTu. Select all that apply:"
      descriptionStyle={{ marginBottom: 18 }}
      leftButtonText="Skip"
      leftButtonAction={onClose}
      rightButtonText="Submit"
      rightButtonAction={() => {
        onSubmit({
          positive: selectedPositive,
          negative: selectedNegative,
          otherText,
        });
      }}
      rightButtonDisabled={selectedPositive.length === 0 && selectedNegative.length === 0}
      closePopup={onClose}
      showClosePopupIcon
      dismissiblePopup={false}
      modalStyle={{ width: 520 }}
    >
      <Box marginBottom={30} paddingHorizontal={40} gap={20}>
        {/* Positive feedback section */}
        <Box gap={10}>
          <Row gap={6} alignItems="center">
            <img src={ThumbsUpIcon} alt="thumbs-up-icon" />
            <Text fontSize={18} bold>
              What went well
            </Text>
          </Row>
          <Row gap={10} flexWrap="wrap">
            {feedbackOptionsPositive.map((option) => (
              <FeedbackItem
                key={option}
                label={option}
                isSelected={selectedPositive.includes(option)}
                onClick={() => toggleOption(option, 'positive')}
              />
            ))}
          </Row>
        </Box>

        {/* Negative feedback section */}
        <Box gap={10}>
          <Row gap={6} alignItems="center">
            <img src={ExclamationIcon} alt="exclamation-icon" />
            <Text fontSize={18} bold>
              Areas to improve
            </Text>
          </Row>
          <Row gap={10} flexWrap="wrap">
            {feedbackOptionsNegative.map((option) => (
              <FeedbackItem
                key={option}
                label={option}
                isSelected={selectedNegative.includes(option)}
                onClick={() => toggleOption(option, 'negative')}
              />
            ))}
          </Row>
        </Box>

        {/* Other input (shown when Other is selected) */}
        <AnimatePresence>
          {showOtherInput && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              style={{ width: '100%', overflow: 'hidden' }}
            >
              <textarea
                style={{
                  height: 80,
                  width: '100%',
                  fontSize: 16,
                  padding: 12,
                  color: Colors.neutral_600,
                  border: `1px solid transparent`,
                  borderRadius: 10,
                  backgroundColor: Colors.neutral_50,
                  boxShadow: 'inset 0px 2px 4px rgba(0, 0, 0, 0.25)',
                  resize: 'none',
                  boxSizing: 'border-box',
                }}
                placeholder="Tell us more..."
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </ConfirmPopup>
  );
};

export const CloseJobFeedbackPopup = memo(CloseJobFeedbackPopupComponent);
